import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  QrCode, Package, Thermometer, CheckCircle, Clock, 
  Scan, Plus, RefreshCw, Camera, AlertTriangle, Truck, LogOut, CameraOff
} from 'lucide-react';
import { Html5Qrcode } from 'html5-qrcode';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProducerApp = () => {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const [shipments, setShipments] = useState([]);
  const [devices, setDevices] = useState([]);
  const [receivers, setReceivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isActivating, setIsActivating] = useState(false);
  const [showNewShipmentDialog, setShowNewShipmentDialog] = useState(false);
  const [showScannerDialog, setShowScannerDialog] = useState(false);
  const [scannerActive, setScannerActive] = useState(false);
  const [newShipment, setNewShipment] = useState({
    device_id: '',
    pallet_id: '',
    sscc: '',
    receiver_id: '',
    origin: '',
    destination: '',
    expected_temp_min: -20,
    expected_temp_max: 8,
  });

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (token) {
      fetchData();
    }
  }, [token]);

  const fetchData = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [shipmentsRes, devicesRes, orgsRes] = await Promise.all([
        axios.get(`${API}/shipments`, { headers }),
        axios.get(`${API}/devices`, { headers }),
        axios.get(`${API}/organizations`, { headers }).catch(() => ({ data: [] }))
      ]);
      setShipments(shipmentsRes.data);
      setDevices(devicesRes.data);
      // Filter receivers from organizations
      setReceivers(orgsRes.data.filter(o => o.type === 'receiver'));
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const [cameraError, setCameraError] = useState(null);
  const html5QrCodeRef = useRef(null);

  const startScanner = async () => {
    try {
      setCameraError(null);
      setScannerActive(true);
      
      // Wait for DOM to render
      await new Promise(resolve => setTimeout(resolve, 100));
      
      html5QrCodeRef.current = new Html5Qrcode('producer-qr-reader');
      
      await html5QrCodeRef.current.start(
        { facingMode: 'environment' },
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
        },
        (decodedText) => {
          handleQrScanned(decodedText);
        },
        (errorMessage) => {
          // Scan error - ignore, keep scanning
        }
      );
    } catch (err) {
      setCameraError(err.message || 'Unable to access camera');
      setScannerActive(false);
    }
  };

  const stopScanner = async () => {
    if (html5QrCodeRef.current && html5QrCodeRef.current.isScanning) {
      try {
        await html5QrCodeRef.current.stop();
      } catch {
        // Scanner may already be stopped
      }
    }
    setScannerActive(false);
    setCameraError(null);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (html5QrCodeRef.current && html5QrCodeRef.current.isScanning) {
        html5QrCodeRef.current.stop().catch(() => {});
      }
    };
  }, []);

  const handleQrScanned = async (qrData) => {
    await stopScanner();
    toast.success(`QR Code scanned: ${qrData}`);
    // Parse GS1 Digital Link format: could be SSCC or pallet ID
    let sscc = qrData;
    if (qrData.includes('sscc/')) {
      sscc = qrData.split('sscc/')[1]?.split('?')[0];
    } else if (qrData.includes('sscc=')) {
      sscc = qrData.split('sscc=')[1]?.split('&')[0];
    }
    setNewShipment(prev => ({ ...prev, sscc: sscc || qrData }));
    setShowScannerDialog(false);
    setShowNewShipmentDialog(true);
  };

  const handleActivateShipment = async () => {
    if (!newShipment.device_id || !newShipment.receiver_id || !newShipment.origin || !newShipment.destination) {
      toast.error('Please fill all required fields');
      return;
    }

    setIsActivating(true);
    try {
      // Generate SSCC if not provided
      const sscc = newShipment.sscc || `00${user.gs1_company_prefix || '7071456'}${Date.now()}`;
      
      const device = devices.find(d => d.device_id === newShipment.device_id);
      
      const payload = {
        sscc,
        pallet_id: device?.pallet_id || newShipment.pallet_id || `PLT-${Date.now()}`,
        device_id: newShipment.device_id,
        receiver_id: newShipment.receiver_id,
        origin: newShipment.origin,
        destination: newShipment.destination,
        expected_temp_min: newShipment.expected_temp_min,
        expected_temp_max: newShipment.expected_temp_max,
      };

      await axios.post(`${API}/shipments`, payload, { headers });
      toast.success('Shipment activated successfully!');
      setShowNewShipmentDialog(false);
      setNewShipment({
        device_id: '',
        pallet_id: '',
        sscc: '',
        receiver_id: '',
        origin: '',
        destination: '',
        expected_temp_min: -20,
        expected_temp_max: 8,
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to activate shipment');
    } finally {
      setIsActivating(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'activated':
      case 'in_transit':
        return 'bg-blue-100 text-blue-700';
      case 'geofence_entered':
        return 'bg-yellow-100 text-yellow-700';
      case 'completed':
        return 'bg-green-100 text-green-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const activeShipments = shipments.filter(s => ['activated', 'in_transit', 'geofence_entered'].includes(s.status));
  const completedShipments = shipments.filter(s => s.status === 'completed');

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 p-4 md:p-8">
      {/* Header */}
      <div className="max-w-4xl mx-auto mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Producer Portal</h1>
            <p className="text-gray-600 mt-1">Activate and track your shipments</p>
          </div>
          <div className="flex items-center gap-2">
            <Badge className="bg-green-500 text-white px-3 py-1">
              {user?.organization_name || 'Producer'}
            </Badge>
            <Button variant="outline" size="sm" onClick={handleLogout}>
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="max-w-4xl mx-auto mb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Scan QR to Activate */}
          <Card className="bg-white/70 backdrop-blur-md border-white/30 shadow-lg hover:shadow-xl transition-all cursor-pointer"
                onClick={() => setShowScannerDialog(true)}>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
                  <Scan className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Scan QR Code</h3>
                  <p className="text-sm text-gray-600">Scan pallet QR to activate shipment</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Manual Entry */}
          <Card className="bg-white/70 backdrop-blur-md border-white/30 shadow-lg hover:shadow-xl transition-all cursor-pointer"
                onClick={() => setShowNewShipmentDialog(true)}>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center">
                  <Plus className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Manual Entry</h3>
                  <p className="text-sm text-gray-600">Create shipment without scanning</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Stats */}
      <div className="max-w-4xl mx-auto mb-8">
        <div className="grid grid-cols-3 gap-4">
          <Card className="bg-white/60 backdrop-blur-md border-white/30">
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-bold text-blue-600">{activeShipments.length}</div>
              <div className="text-sm text-gray-600">Active</div>
            </CardContent>
          </Card>
          <Card className="bg-white/60 backdrop-blur-md border-white/30">
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-bold text-green-600">{completedShipments.length}</div>
              <div className="text-sm text-gray-600">Completed</div>
            </CardContent>
          </Card>
          <Card className="bg-white/60 backdrop-blur-md border-white/30">
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-bold text-gray-600">{devices.length}</div>
              <div className="text-sm text-gray-600">Devices</div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Active Shipments List */}
      <div className="max-w-4xl mx-auto">
        <Card className="bg-white/70 backdrop-blur-md border-white/30 shadow-lg">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>My Shipments</CardTitle>
              <CardDescription>Shipments you have activated</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={fetchData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-gray-500">Loading...</div>
            ) : shipments.length === 0 ? (
              <div className="text-center py-8">
                <Package className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-500">No shipments yet</p>
                <p className="text-sm text-gray-400 mt-2">Scan a QR code or manually create a shipment to get started</p>
              </div>
            ) : (
              <div className="space-y-3">
                {shipments.map((shipment) => (
                  <div key={shipment.id} className="p-4 bg-white/50 rounded-xl border border-gray-100">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                          <Truck className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <div className="font-mono text-sm font-medium">{shipment.sscc?.slice(-12)}</div>
                          <div className="text-xs text-gray-500">{shipment.device_id}</div>
                        </div>
                      </div>
                      <Badge className={getStatusColor(shipment.status)}>
                        {shipment.status?.replace('_', ' ')}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <div className="text-gray-600">
                        {shipment.origin} → {shipment.destination}
                      </div>
                      <div className="flex items-center gap-2 text-gray-500">
                        <Thermometer className="w-4 h-4" />
                        {shipment.expected_temp_min}°C to {shipment.expected_temp_max}°C
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* QR Scanner Dialog */}
      <Dialog open={showScannerDialog} onOpenChange={setShowScannerDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Camera className="w-5 h-5" />
              Scan Pallet QR Code
            </DialogTitle>
            <DialogDescription>
              Point your camera at the pallet's QR code to activate shipment
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            {cameraError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
                <CameraOff className="w-4 h-4" />
                {cameraError}
              </div>
            )}
            
            {!scannerActive ? (
              <div className="flex flex-col items-center gap-4">
                <div className="w-48 h-48 bg-gray-100 rounded-2xl flex items-center justify-center">
                  <QrCode className="w-24 h-24 text-gray-400" />
                </div>
                <Button onClick={startScanner} className="bg-green-600 hover:bg-green-700">
                  <Camera className="w-4 h-4 mr-2" />
                  Start Camera
                </Button>
              </div>
            ) : (
              <div className="w-full">
                <div id="producer-qr-reader" className="w-full rounded-xl overflow-hidden"></div>
                <Button 
                  variant="outline" 
                  className="w-full mt-4" 
                  onClick={stopScanner}
                >
                  Stop Camera
                </Button>
              </div>
            )}
          </div>

          <div className="border-t pt-4">
            <p className="text-sm text-gray-500 mb-2">Or enter SSCC manually:</p>
            <div className="flex gap-2">
              <Input 
                placeholder="Enter SSCC code"
                value={newShipment.sscc}
                onChange={(e) => setNewShipment(prev => ({ ...prev, sscc: e.target.value }))}
              />
              <Button onClick={() => {
                setShowScannerDialog(false);
                setShowNewShipmentDialog(true);
              }}>
                Continue
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* New Shipment Dialog */}
      <Dialog open={showNewShipmentDialog} onOpenChange={setShowNewShipmentDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5" />
              Activate New Shipment
            </DialogTitle>
            <DialogDescription>
              Fill in the details to activate this shipment
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {newShipment.sscc && (
              <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                <div className="text-sm text-green-600 font-medium">SSCC Code</div>
                <div className="font-mono text-lg">{newShipment.sscc}</div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Select Device/Pallet *</Label>
                <Select 
                  value={newShipment.device_id} 
                  onValueChange={(v) => setNewShipment(prev => ({ ...prev, device_id: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select device" />
                  </SelectTrigger>
                  <SelectContent>
                    {devices.map((device) => (
                      <SelectItem key={device.device_id} value={device.device_id}>
                        {device.name} ({device.pallet_id})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Receiver *</Label>
                <Select 
                  value={newShipment.receiver_id} 
                  onValueChange={(v) => setNewShipment(prev => ({ ...prev, receiver_id: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select receiver" />
                  </SelectTrigger>
                  <SelectContent>
                    {receivers.length > 0 ? receivers.map((org) => (
                      <SelectItem key={org.id} value={org.id}>
                        {org.name}
                      </SelectItem>
                    )) : (
                      <SelectItem value="demo-receiver">Demo Receiver</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Origin Location *</Label>
                <Input 
                  placeholder="e.g., FishCo Bergen"
                  value={newShipment.origin}
                  onChange={(e) => setNewShipment(prev => ({ ...prev, origin: e.target.value }))}
                />
              </div>

              <div className="space-y-2">
                <Label>Destination *</Label>
                <Input 
                  placeholder="e.g., Rema 1000 Oslo"
                  value={newShipment.destination}
                  onChange={(e) => setNewShipment(prev => ({ ...prev, destination: e.target.value }))}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Min Temperature (°C)</Label>
                <Input 
                  type="number"
                  value={newShipment.expected_temp_min}
                  onChange={(e) => setNewShipment(prev => ({ ...prev, expected_temp_min: parseFloat(e.target.value) }))}
                />
              </div>

              <div className="space-y-2">
                <Label>Max Temperature (°C)</Label>
                <Input 
                  type="number"
                  value={newShipment.expected_temp_max}
                  onChange={(e) => setNewShipment(prev => ({ ...prev, expected_temp_max: parseFloat(e.target.value) }))}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewShipmentDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleActivateShipment} 
              disabled={isActivating}
              className="bg-green-600 hover:bg-green-700"
            >
              {isActivating ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Activating...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Activate Shipment
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProducerApp;
